import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  useMediaQuery
} from '@mui/material';
import { renderAsync } from 'docx-preview';
import { useTheme } from '@mui/material/styles';
import {
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  OpenInNew as OpenInNewIcon,
  Business as BusinessIcon,
  SupervisorAccount as SupervisorIcon,
  AdminPanelSettings as AdminIcon,
  Assessment as AssessmentIcon,
  Emergency as EmergencyIcon,
  Warning as WarningIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../config/api';
import KnowledgeAssistant from './Insights/KnowledgeAssistant';

const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getPersonalDocumentKind = (doc) => {
  const mime = String(doc.mime_type || '').toLowerCase();
  const name = String(doc.filename || '').toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  if (mime.includes('word') || name.endsWith('.doc') || name.endsWith('.docx')) return 'docx';
  return 'other';
};

const DocumentsPage = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreenViewer = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedDocument, setSelectedDocument] = useState(null);
  // TKT-247: DOCX is now rendered inline, client-side, via docx-preview.
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxError, setDocxError] = useState(false);
  const docxContainerRef = useRef(null);
  // TKT-262: personal HR documents belonging to the logged-in employee.
  const [myDocuments, setMyDocuments] = useState([]);
  const [myDocumentsLoading, setMyDocumentsLoading] = useState(true);
  const [myDocumentsError, setMyDocumentsError] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState('');

  // Document data with metadata
  const documents = [
    // TKT-91: the Company Policy booklet, so staff can read it any time, not
    // only while signing the Company Policy form (agreed with Naomi, Nov 2).
    {
      id: 8,
      filename: 'legal-docs/Paramount_Landscaping_Company_Policy_V42020.pdf',
      titleKey: 'documentsPage.documents.companyPolicy.title',
      descriptionKey: 'documentsPage.documents.companyPolicy.description',
      type: 'pdf',
      icon: <PdfIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
      year: '2020',
      roleKey: 'documentsPage.roles.allStaff'
    },
    {
      id: 9,
      filename: 'legal-docs/Paramount_Landscaping_Company_Policy_V4_2020SP.pdf',
      titleKey: 'documentsPage.documents.companyPolicySpanish.title',
      descriptionKey: 'documentsPage.documents.companyPolicySpanish.description',
      type: 'pdf',
      icon: <PdfIcon sx={{ fontSize: 40, color: '#ff5722' }} />,
      year: '2020',
      roleKey: 'documentsPage.roles.allStaffSpanish'
    },
    {
      id: 1,
      filename: '2025PL_SOPManuel_PMSALESMANAGER_V1.docx',
      titleKey: 'documentsPage.documents.salesManagerSop.title',
      descriptionKey: 'documentsPage.documents.salesManagerSop.description',
      type: 'docx',
      icon: <SupervisorIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      year: '2025',
      roleKey: 'documentsPage.roles.salesManager'
    },
    {
      id: 2,
      filename: '2025Q3_ParamountLandscaping_OrgChart.pdf',
      titleKey: 'documentsPage.documents.orgChart.title',
      descriptionKey: 'documentsPage.documents.orgChart.description',
      type: 'pdf',
      icon: <AssessmentIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
      year: '2025',
      roleKey: 'documentsPage.roles.allStaff'
    },
    {
      id: 3,
      filename: 'PL2024_SOPmanual_Operations ManagerV3_2.docx',
      titleKey: 'documentsPage.documents.operationsManagerSop.title',
      descriptionKey: 'documentsPage.documents.operationsManagerSop.description',
      type: 'docx',
      icon: <BusinessIcon sx={{ fontSize: 40, color: '#2e7d32' }} />,
      year: '2024',
      roleKey: 'documentsPage.roles.operationsManager'
    },
    {
      id: 4,
      filename: 'PL2024_SOPmanual_PresidentV4.docx',
      titleKey: 'documentsPage.documents.presidentSop.title',
      descriptionKey: 'documentsPage.documents.presidentSop.description',
      type: 'docx',
      icon: <AdminIcon sx={{ fontSize: 40, color: '#7b1fa2' }} />,
      year: '2024',
      roleKey: 'documentsPage.roles.president'
    },
    {
      id: 5,
      filename: 'Paramount_Emergency_Policies.docx',
      titleKey: 'documentsPage.documents.emergencyEnglish.title',
      descriptionKey: 'documentsPage.documents.emergencyEnglish.description',
      type: 'docx',
      icon: <EmergencyIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
      year: '2025',
      roleKey: 'documentsPage.roles.allStaff'
    },
    {
      id: 6,
      filename: 'Paramount_Emergency_Policies_Spanish.docx',
      titleKey: 'documentsPage.documents.emergencySpanish.title',
      descriptionKey: 'documentsPage.documents.emergencySpanish.description',
      type: 'docx',
      icon: <WarningIcon sx={{ fontSize: 40, color: '#ff5722' }} />,
      year: '2025',
      roleKey: 'documentsPage.roles.allStaffSpanish'
    },
    {
      id: 7,
      filename: 'Paramount_Emergency_Policies_Bilingual.docx',
      titleKey: 'documentsPage.documents.emergencyBilingual.title',
      descriptionKey: 'documentsPage.documents.emergencyBilingual.description',
      type: 'docx',
      icon: <LanguageIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      year: '2025',
      roleKey: 'documentsPage.roles.allStaffBilingual'
    }
  ];

  useEffect(() => {
    let cancelled = false;

    const loadMyDocuments = async () => {
      setMyDocumentsLoading(true);
      setMyDocumentsError(false);
      try {
        const response = await makeApiCall('api/hr/documents/mine');
        if (!response.ok) {
          throw new Error(`Failed to load personal documents (${response.status})`);
        }
        const data = await response.json();
        if (!cancelled) {
          setMyDocuments(Array.isArray(data.documents) ? data.documents : []);
        }
      } catch (err) {
        console.error('Error loading personal documents:', err);
        if (!cancelled) {
          setMyDocuments([]);
          setMyDocumentsError(true);
        }
      } finally {
        if (!cancelled) {
          setMyDocumentsLoading(false);
        }
      }
    };

    loadMyDocuments();
    return () => {
      cancelled = true;
    };
  }, []);

  // Files can sit in a folder (e.g. legal-docs/...), so encode each part of
  // the path rather than the whole thing (which would encode the "/").
  const getDocumentUrl = (filename) => `${process.env.PUBLIC_URL || ''}/${String(filename).split('/').map(encodeURIComponent).join('/')}`;

  const getAbsoluteDocumentUrl = (filename) => new URL(getDocumentUrl(filename), window.location.origin).href;

  const getBrowserViewerUrl = (doc) => {
    if (doc.type === 'docx') {
      return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(getAbsoluteDocumentUrl(doc.filename))}`;
    }

    return getDocumentUrl(doc.filename);
  };

  const handleView = (doc) => {
    setSelectedDocument(doc);
  };

  const handleDownloadPersonalDocument = async (doc) => {
    if (!doc?.id) return;
    setDownloadError('');
    setDownloadingId(doc.id);
    try {
      const response = await makeApiCall(`api/hr/documents/mine/${doc.id}/download`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Failed to download (${response.status})`);
      }
      const downloadUrl = data.download_url || data.download_url;
      if (!downloadUrl) {
        throw new Error('No download URL returned');
      }
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = data.filename || doc.filename || 'document';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading personal document:', err);
      setDownloadError(t('documentsPage.downloadFailed'));
    } finally {
      setDownloadingId(null);
    }
  };

  // TKT-247: render DOCX inline with the client-side docx-preview library
  // instead of an external iframe viewer. The previous approaches both
  // failed on Heroku-hosted files: the Office Online embed (TKT-198) and
  // then Google Docs Viewer both showed "This content is blocked" because
  // those services must fetch the source URL themselves and our static
  // assets aren't served with the permissive/anonymous access they need.
  // docx-preview fetches the file same-origin in the browser and renders
  // the document body to the DOM, so there is no third-party dependency.
  // PDFs still render natively in an iframe; "Open in Browser" (Office
  // Online) stays as an advanced-user fallback.
  useEffect(() => {
    if (!selectedDocument || selectedDocument.type !== 'docx') return;

    let cancelled = false;
    const container = docxContainerRef.current;
    if (container) container.innerHTML = '';
    setDocxError(false);
    setDocxLoading(true);

    (async () => {
      try {
        const response = await fetch(getDocumentUrl(selectedDocument.filename));
        if (!response.ok) throw new Error(`Failed to fetch document (${response.status})`);
        const buffer = await response.arrayBuffer();
        if (cancelled) return;
        const target = docxContainerRef.current;
        if (!target) return;
        await renderAsync(buffer, target, undefined, {
          className: 'docx',
          inWrapper: true,
          ignoreLastRenderedPageBreak: true,
        });
        if (!cancelled) setDocxLoading(false);
      } catch (err) {
        console.error('Error rendering DOCX:', err);
        if (!cancelled) {
          setDocxError(true);
          setDocxLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedDocument]);

  const handleOpenInBrowser = (doc) => {
    if (!doc) return;
    window.open(getBrowserViewerUrl(doc), '_blank', 'noopener,noreferrer');
  };

  const handleDownload = (filename) => {
    // TKT-233: same-origin file + download attribute = in-place download;
    // target=_blank was spawning an extra tab on iPad.
    const link = document.createElement('a');
    link.href = getDocumentUrl(filename);
    link.download = String(filename).split('/').pop();
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'pdf':
        return <PdfIcon sx={{ color: '#d32f2f', mr: 1 }} />;
      case 'docx':
        return <DescriptionIcon sx={{ color: '#1976d2', mr: 1 }} />;
      default:
        return <DescriptionIcon sx={{ color: '#666', mr: 1 }} />;
    }
  };

  const renderDocumentViewerDialog = () => {
    if (!selectedDocument) return null;

    const documentTitle = t(selectedDocument.titleKey);
    const isDocx = selectedDocument.type === 'docx';

    return (
      <Dialog
        open={Boolean(selectedDocument)}
        onClose={() => setSelectedDocument(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreenViewer}
        aria-labelledby="company-document-viewer-title"
      >
        <DialogTitle id="company-document-viewer-title">
          <Box display="flex" alignItems="center" gap={1}>
            {getFileTypeIcon(selectedDocument.type)}
            <Typography component="span" variant="h6" sx={{ wordBreak: 'break-word' }}>
              {documentTitle}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
          {isDocx ? (
            <Box
              sx={{
                width: '100%',
                height: { xs: '72vh', sm: '70vh' },
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: '#f5f5f5'
              }}
            >
              {docxLoading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                  <CircularProgress aria-label={t('documentsPage.loadingDocument')} />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {t('documentsPage.loadingDocument')}
                  </Typography>
                </Box>
              )}
              {docxError && (
                <Box sx={{ p: 2 }}>
                  <Alert severity="warning">{t('documentsPage.documentLoadError')}</Alert>
                </Box>
              )}
              <Box
                ref={docxContainerRef}
                aria-label={t('documentsPage.viewerAriaLabel', { title: documentTitle })}
                sx={{ display: docxLoading || docxError ? 'none' : 'block' }}
              />
            </Box>
          ) : (
            <Box
              component="iframe"
              src={getDocumentUrl(selectedDocument.filename)}
              title={t('documentsPage.viewerTitle', { title: documentTitle })}
              aria-label={t('documentsPage.viewerAriaLabel', { title: documentTitle })}
              sx={{
                width: '100%',
                height: { xs: '72vh', sm: '70vh' },
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: '#fff'
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: 'stretch', gap: 1, p: 2 }}>
          <Button onClick={() => setSelectedDocument(null)} aria-label={t('documentsPage.closeViewerAriaLabel')}>
            {t('common.close')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(selectedDocument.filename)}
            aria-label={t('documentsPage.downloadAriaLabel', { title: documentTitle })}
          >
            {t('documentsPage.download')}
          </Button>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={() => handleOpenInBrowser(selectedDocument)}
            aria-label={t('documentsPage.openInBrowserAriaLabel', { title: documentTitle })}
            sx={{ bgcolor: '#000062', '&:hover': { bgcolor: '#000043' } }}
          >
            {t('documentsPage.openInBrowser')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', mx: 0, px: 0 }} data-demo-target="page-documents-root">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 2, 
            fontWeight: 'bold', 
            color: '#000062', 
            fontSize: { xs: '1.5rem', md: '2.125rem' } 
          }}
        >
          {t('documentsPage.title')}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#666', 
            fontSize: '1.1rem',
            maxWidth: '800px' 
          }}
        >
          {t('documentsPage.description')}
        </Typography>
      </Box>

      <KnowledgeAssistant />

      {/* TKT-262: personal HR documents for the logged-in employee */}
      <Box
        component="section"
        aria-labelledby="my-documents-heading"
        sx={{ mb: 5 }}
      >
        <Typography
          id="my-documents-heading"
          variant="h5"
          sx={{ mb: 1, fontWeight: 'bold', color: '#000062', fontSize: { xs: '1.25rem', md: '1.5rem' } }}
        >
          {t('documentsPage.myDocumentsTitle')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2, maxWidth: '800px' }}>
          {t('documentsPage.myDocumentsDescription')}
        </Typography>

        {downloadError && (
          <Alert
            severity="error"
            onClose={() => setDownloadError('')}
            sx={{ mb: 2 }}
            role="alert"
          >
            {downloadError}
          </Alert>
        )}

        {myDocumentsLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={28} aria-label={t('documentsPage.loadingMyDocuments')} />
            <Typography variant="body2" sx={{ color: '#666' }}>
              {t('documentsPage.loadingMyDocuments')}
            </Typography>
          </Box>
        )}

        {!myDocumentsLoading && myDocumentsError && (
          <Alert severity="warning" sx={{ mb: 2 }} role="alert">
            {t('documentsPage.loadMyDocumentsError')}
          </Alert>
        )}

        {!myDocumentsLoading && !myDocumentsError && myDocuments.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }} role="status">
            {t('documentsPage.emptyMyDocuments')}
          </Alert>
        )}

        {!myDocumentsLoading && !myDocumentsError && myDocuments.length > 0 && (
          <Grid container spacing={2}>
            {myDocuments.map((doc) => {
              const kind = getPersonalDocumentKind(doc);
              const title = doc.filename || t('documentsPage.untitledDocument');
              const typeKey = `documentsPage.documentTypes.${doc.document_type || 'general'}`;
              const uploadedLabel = doc.uploaded_at
                ? t('documentsPage.uploadedOn', {
                    date: new Date(doc.uploaded_at).toLocaleDateString(i18n.language || 'en', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })
                  })
                : '';
              const sizeLabel = formatFileSize(doc.file_size);
              const isDownloading = downloadingId === doc.id;

              return (
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} key={doc.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                        {getFileTypeIcon(kind)}
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 'bold',
                          color: '#000062',
                          textAlign: 'center',
                          wordBreak: 'break-word',
                          mb: 1
                        }}
                      >
                        {title}
                      </Typography>
                      {doc.description && (
                        <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mb: 1.5 }}>
                          {doc.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <Chip
                          size="small"
                          label={t(typeKey, { defaultValue: doc.document_type || t('documentsPage.documentTypes.general') })}
                          aria-label={t('documentsPage.documentTypeAria', { type: t(typeKey, { defaultValue: doc.document_type || '' }) })}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', fontSize: '0.85rem' }}>
                        {[uploadedLabel, sizeLabel].filter(Boolean).join(' • ')}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={isDownloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                        onClick={() => handleDownloadPersonalDocument(doc)}
                        disabled={isDownloading}
                        aria-label={t('documentsPage.downloadPersonalAria', { title })}
                        sx={{
                          bgcolor: '#000062',
                          color: 'white',
                          py: 1.1,
                          textTransform: 'none',
                          borderRadius: 2,
                          '&:hover': { bgcolor: '#000043' }
                        }}
                      >
                        {isDownloading ? t('documentsPage.downloading') : t('documentsPage.download')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* Company documents */}
      <Typography
        id="company-documents-heading"
        variant="h5"
        sx={{ mb: 2, fontWeight: 'bold', color: '#000062', fontSize: { xs: '1.25rem', md: '1.5rem' } }}
      >
        {t('documentsPage.companyDocumentsTitle')}
      </Typography>

      {/* Documents Grid */}
      <Grid container spacing={3}>
        {documents.map((doc) => (
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }} key={doc.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                },
                border: '1px solid #e0e0e0',
                borderRadius: 2
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                {/* Document Icon */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  {doc.icon}
                </Box>

                {/* Document Title */}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: 'bold',
                    color: '#000062',
                    textAlign: 'center',
                    fontSize: '1.1rem',
                    lineHeight: 1.3
                  }}
                >
                  {t(doc.titleKey)}
                </Typography>

                {/* Document Description */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666', 
                    mb: 2,
                    textAlign: 'center',
                    lineHeight: 1.4
                  }}
                >
                  {t(doc.descriptionKey)}
                </Typography>

                {/* File Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  {getFileTypeIcon(doc.type)}
                  <Typography variant="body2" sx={{ color: '#888', fontSize: '0.9rem' }}>
                    {t('documentsPage.fileMeta', { type: doc.type.toUpperCase(), year: doc.year })}
                  </Typography>
                </Box>

                {/* Role Badge */}
                <Box sx={{ textAlign: 'center' }}>
                  <Paper
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 0.5,
                      bgcolor: '#f5f5f5',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#555', fontWeight: 'medium' }}>
                      {t(doc.roleKey)}
                    </Typography>
                  </Paper>
                </Box>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0 }}>
                <Stack spacing={1} sx={{ width: '100%' }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<ViewIcon />}
                    onClick={() => handleView(doc)}
                    aria-label={t(doc.type === 'pdf' ? 'documentsPage.viewPdfAriaLabel' : 'documentsPage.viewDocumentAriaLabel', { title: t(doc.titleKey) })}
                    sx={{
                      bgcolor: '#000062',
                      color: 'white',
                      py: 1.2,
                      fontSize: '0.95rem',
                      fontWeight: 'medium',
                      textTransform: 'none',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: '#000043',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,98,0.3)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {doc.type === 'pdf' ? t('documentsPage.viewPdf') : t('documentsPage.viewDocument')}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(doc.filename)}
                    aria-label={t('documentsPage.downloadAriaLabel', { title: t(doc.titleKey) })}
                    sx={{
                      py: 1.2,
                      fontSize: '0.95rem',
                      fontWeight: 'medium',
                      textTransform: 'none',
                      borderRadius: 2,
                      borderColor: '#000062',
                      color: '#000062'
                    }}
                  >
                    {t('documentsPage.download')}
                  </Button>
                </Stack>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Info Section */}
      <Box sx={{ mt: 6, p: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#000062', fontWeight: 'bold' }}>
          {t('documentsPage.informationTitle')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
          <strong>{t('documentsPage.information.sopLabel')}</strong> {t('documentsPage.information.sopText')}
          <br /><br />
          <strong>{t('documentsPage.information.emergencyLabel')}</strong> {t('documentsPage.information.emergencyText')}
          <br /><br />
          <strong>{t('documentsPage.information.orgChartLabel')}</strong> {t('documentsPage.information.orgChartText')}
          <br /><br />
          <strong>{t('documentsPage.information.updatesLabel')}</strong> {t('documentsPage.information.updatesText')}
        </Typography>
      </Box>
      {renderDocumentViewerDialog()}
    </Box>
  );
};

export default DocumentsPage;
